import { StrategyConstraint, StrategyNodeAttributeTemplate } from '~/api-services/AIStrategyBuilderService/types';
import i18n from '~/i18n';

export function collectMissingFields(
    attrs: StrategyNodeAttributeTemplate[],
    value: Record<string, unknown>,
    prefix = ''
): string[] {
    const missing: string[] = [];
    for (const attr of attrs) {
        const path = prefix ? `${prefix}.${attr.name}` : attr.name;
        const raw = value[attr.name];
        if (attr.fields && attr.fields.length > 0) {
            const nested = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
            missing.push(...collectMissingFields(attr.fields, nested, path));
            continue;
        }
        if (raw === undefined || raw === null || raw === '') {
            missing.push(path);
            continue;
        }
        if (attr.valueType === 'int' || attr.valueType === 'float') {
            const n = typeof raw === 'number' ? raw : Number(raw);
            if (!Number.isFinite(n)) {
                missing.push(path);
            }
        }
    }
    return missing;
}

const CONSTRAINT_OPS: Record<string, { test: (l: number, r: number) => boolean; word: string }> = {
    lessThan: { test: (l, r) => l < r, word: 'less than' },
    lessThanOrEqual: { test: (l, r) => l <= r, word: 'less than or equal to' },
    greaterThan: { test: (l, r) => l > r, word: 'greater than' },
    greaterThanOrEqual: { test: (l, r) => l >= r, word: 'greater than or equal to' },
};

// Resolve a (possibly dotted) field path to a finite number, or undefined.
function numAtPath(value: Record<string, unknown>, path: string): number | undefined {
    let cur: unknown = value;
    for (const part of path.split('.')) {
        if (!cur || typeof cur !== 'object') {
            return undefined;
        }
        cur = (cur as Record<string, unknown>)[part];
    }
    const n = typeof cur === 'number' ? cur : Number(cur);
    return Number.isFinite(n) ? n : undefined;
}

// Collect API-provided validation failures for a node value: numeric min/max
// bounds on leaf fields plus cross-field `constraints` (lessThan, …) at every scope.
export function collectConstraintViolations(
    attrs: StrategyNodeAttributeTemplate[],
    constraints: StrategyConstraint[] | undefined,
    value: Record<string, unknown>,
    prefix = ''
): string[] {
    const out: string[] = [];
    const label = (name: string) => (prefix ? `${prefix}.${name}` : name);
    for (const c of constraints ?? []) {
        const op = CONSTRAINT_OPS[c.type];
        const left = numAtPath(value, c.left);
        const right = numAtPath(value, c.right);
        if (op && left !== undefined && right !== undefined && !op.test(left, right)) {
            out.push(`${label(c.left)} must be ${op.word} ${label(c.right)}`);
        }
    }
    for (const attr of attrs) {
        const raw = value[attr.name];
        if (attr.fields && attr.fields.length > 0) {
            const nested = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
            out.push(...collectConstraintViolations(attr.fields, attr.constraints, nested, label(attr.name)));
            continue;
        }
        if (attr.valueType === 'int' || attr.valueType === 'float') {
            const n = typeof raw === 'number' ? raw : Number(raw);
            if (Number.isFinite(n)) {
                if (attr.minValue !== undefined && n < attr.minValue) {
                    out.push(`${label(attr.name)} must be ≥ ${attr.minValue}`);
                }
                if (attr.maxValue !== undefined && n > attr.maxValue) {
                    out.push(`${label(attr.name)} must be ≤ ${attr.maxValue}`);
                }
            }
        }
    }
    return out;
}

// Map the engine's structured, distance-focused validation error (errorContext = {field, rule,
// limit, precision}) into a precise UI message, e.g. "Take profit must be at least 0.003".
export function strategyValidationMessage(data: unknown): string | null {
    const ctx = (data as { errorContext?: Record<string, unknown> } | undefined)?.errorContext;
    if (!ctx) {
        return null;
    }
    const field = ctx.field as string | undefined;
    const rule = ctx.rule as string | undefined;
    const limit = ctx.limit;
    if (!field || !rule || limit === undefined || limit === null) {
        return null;
    }
    const precision = typeof ctx.precision === 'number' ? ctx.precision : undefined;
    const limitText = precision !== undefined ? Number(limit).toFixed(precision) : String(limit);
    const labels: Record<string, string> = {
        takeProfit: i18n.t('take-profit') ?? 'Take profit',
        stopLoss: i18n.t('stop-loss') ?? 'Stop loss',
        quantity: i18n.t('quantity') ?? 'Quantity',
    };
    const label = labels[field] ?? field;
    const bound = rule === 'MIN' ? 'at least' : 'at most';
    return `${label} must be ${bound} ${limitText}`;
}

function translated(key: string): string | null {
    const value = i18n.t(key);
    return !value || value === key ? null : value;
}

export function strategyTradingDisabledMessage(data: unknown): string | null {
    const envelope = data as { errorCode?: string; errorContext?: { cantTradeReason?: unknown } } | undefined;
    if (envelope?.errorCode !== 'CantTrade') {
        return null;
    }
    const reason = envelope?.errorContext?.cantTradeReason;
    const perReason = typeof reason === 'string' && reason ? translated(`error-CantTrade-${reason}`) : null;
    return perReason ?? i18n.t('ai-strategy-trading-disabled');
}

export function strategyActionErrorMessage(data: unknown, fallback = 'Action failed'): string {
    const envelope = data as { errorCode?: string; message?: string } | undefined;
    return (
        strategyTradingDisabledMessage(data) ??
        strategyValidationMessage(data) ??
        envelope?.message ??
        envelope?.errorCode ??
        fallback
    );
}

// Read the per-account create cooldown from a RATE_LIMITED error envelope
// (errorContext = {secondsLeft}). Returns the seconds remaining, or null if not a cooldown error.
export function strategyCooldownSeconds(data: unknown): number | null {
    const envelope = data as { errorCode?: string; errorContext?: { secondsLeft?: unknown } } | undefined;
    const secondsLeft = envelope?.errorContext?.secondsLeft;
    if (envelope?.errorCode !== 'RATE_LIMITED' || typeof secondsLeft !== 'number') {
        return null;
    }
    return Math.max(0, Math.ceil(secondsLeft));
}
